import { useEffect, useMemo, useState, useSyncExternalStore, type ReactElement, type ReactNode } from 'react';
import type { Ecran } from '../../api/contrat.js';
import { Lien } from '../../app/navigation.js';
import { prenomDe, useNomCourant } from '../../app/role-courant.js';
import { CIBLE_MIN, COULEURS, styleBouton, styleBoutonPrincipal } from '../../app/styles.js';
import { creerControleur } from './controleur.js';
import {
  MESSAGE_ECHEC_EXPORT,
  MESSAGE_EXPORT,
  construireCsvPointTrimestriel,
  nomFichierPointTrimestriel,
  telechargerCsv,
} from './export-csv.js';
import type { SourcePilotage } from './source.js';
import { VueDecisions } from './VueDecisions.js';
import { VuePilotage } from './VuePilotage.js';

export type VuePilotageChoisie = 'pilotage' | 'decisions';

/** Largeur de la fenêtre, remesurée à chaque redimensionnement : la coquille ne la transmet pas aux écrans. */
function useLargeur(): number {
  const lire = (): number => globalThis.innerWidth ?? 1280;
  const [largeur, setLargeur] = useState(lire);
  useEffect(() => {
    const mesurer = (): void => setLargeur(lire());
    mesurer();
    globalThis.addEventListener?.('resize', mesurer);
    return () => globalThis.removeEventListener?.('resize', mesurer);
  }, []);
  return largeur;
}

/** Date du jour dans le fuseau de l'appareil (jamais UTC : un blocage de la veille ne doit pas gagner un jour). */
function aujourdhuiLocal(): string {
  const d = new Date();
  const deux = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${deux(d.getMonth() + 1)}-${deux(d.getDate())}`;
}

/** Date du jour en toutes lettres, pour la salutation (« vendredi 30 octobre 2026 »). */
function dateDuJour(): string {
  return new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

const TEXTES: Readonly<Record<VuePilotageChoisie, { composant: string; chargement: string }>> = {
  pilotage: { composant: 'pilotage-isabelle', chargement: 'Chargement du tableau de bord en cours…' },
  decisions: { composant: 'decisions-isabelle', chargement: 'Chargement des décisions en cours…' },
};

/** D01 et M01 branchés sur une source (injectable pour les tests) : états de chargement, d'erreur, écritures annoncées. */
export function EcranAvecSource({
  ecran,
  vue,
  source,
  largeur,
  telecharger = telechargerCsv,
  apresDecisions = null,
}: {
  ecran: Ecran;
  vue: VuePilotageChoisie;
  source: SourcePilotage;
  largeur?: number;
  /** Remise du fichier au navigateur ; injectable pour les tests. */
  telecharger?: (nom: string, contenu: string) => void;
  /** Bloc placé entre les décisions et la fin du tableau de bord (comptes rendus à faire). */
  apresDecisions?: ReactNode;
}): ReactElement {
  const nom = useNomCourant();
  const prenom = prenomDe(nom);
  const salutation = vue === 'pilotage' && prenom ? `Bonjour ${prenom}` : ecran.titre;
  const [exportEnvoye, setExportEnvoye] = useState<{ message: string; erreur: boolean } | null>(null);
  const controleur = useMemo(() => creerControleur(source), [source]);
  useEffect(() => {
    void controleur.charger();
  }, [controleur]);
  const etat = useSyncExternalStore(controleur.abonner, controleur.etat, controleur.etat);
  const mesuree = useLargeur();
  const titres = useMemo(
    () => Object.fromEntries((etat.donnees?.tableau.dossiers ?? []).map((d) => [d.id, d.title] as const)),
    [etat.donnees],
  );
  const textes = TEXTES[vue];
  const decisions = etat.donnees ? (
    <VueDecisions
      donnees={etat.donnees}
      enCours={etat.enCours}
      titresDossiers={titres}
      onTrancher={(id, option) => void controleur.trancher(id, option)}
      onValiderRelance={(id, decision) => void controleur.validerRelance(id, decision)}
    />
  ) : null;
  return (
    <section data-ecran={ecran.id} data-composant={textes.composant} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div>
          {vue === 'pilotage' && prenom ? (
            <p data-date-jour="" style={{ margin: 0, color: COULEURS.ardoise, fontSize: 14 }}>
              {dateDuJour()}
            </p>
          ) : null}
          <h1 style={{ margin: 0, color: COULEURS.marine, fontSize: 28 }}>{salutation}</h1>
        </div>
        {vue === 'pilotage' ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {etat.statut === 'pret' && etat.donnees ? (
              <button
                type="button"
                style={styleBouton}
                onClick={() => {
                  const donnees = etat.donnees;
                  if (!donnees) return;
                  const jour = aujourdhuiLocal();
                  try {
                    telecharger(nomFichierPointTrimestriel(jour), construireCsvPointTrimestriel(donnees, jour));
                    setExportEnvoye({ message: MESSAGE_EXPORT, erreur: false });
                  } catch {
                    setExportEnvoye({ message: MESSAGE_ECHEC_EXPORT, erreur: true });
                  }
                }}
              >
                Exporter le point trimestriel
              </button>
            ) : null}
            <Lien
              vers="/dossier?nouveau=1"
              enfants="Nouveau dossier"
              style={{
                ...styleBoutonPrincipal,
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: CIBLE_MIN,
                textDecoration: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>
        ) : null}
      </div>
      {etat.statut === 'chargement' ? <p role="status">{textes.chargement}</p> : null}
      {etat.statut === 'erreur' ? (
        <div role="alert" style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <p style={{ margin: 0, color: COULEURS.rouge }}>{etat.message}</p>
          <button type="button" style={styleBouton} onClick={() => void controleur.charger()}>
            Réessayer
          </button>
        </div>
      ) : null}
      {etat.statut === 'pret' && etat.message ? (
        <p
          role={etat.messageEstErreur ? 'alert' : 'status'}
          style={{ margin: 0, color: etat.messageEstErreur ? COULEURS.rouge : COULEURS.marine }}
        >
          {etat.message}
        </p>
      ) : null}
      {vue === 'pilotage' && exportEnvoye ? (
        <p
          role={exportEnvoye.erreur ? 'alert' : 'status'}
          style={{ margin: 0, color: exportEnvoye.erreur ? COULEURS.rouge : COULEURS.marine }}
        >
          {exportEnvoye.message}
        </p>
      ) : null}
      {etat.statut === 'pret' && etat.donnees && vue === 'pilotage' ? (
        <>
          <VuePilotage
            donnees={etat.donnees}
            filtreStatut={etat.filtreStatut}
            aujourdhui={aujourdhuiLocal()}
            largeur={largeur ?? mesuree}
            onFiltre={controleur.filtrerStatut}
            partie="haut"
          />
          <h2 style={{ margin: 0, fontSize: 18, color: COULEURS.marine }}>Vos décisions</h2>
          {decisions}
          {apresDecisions}
          <VuePilotage
            donnees={etat.donnees}
            filtreStatut={etat.filtreStatut}
            aujourdhui={aujourdhuiLocal()}
            largeur={largeur ?? mesuree}
            onFiltre={controleur.filtrerStatut}
            partie="bas"
          />
        </>
      ) : null}
      {etat.statut === 'pret' && etat.donnees && vue === 'decisions' ? decisions : null}
    </section>
  );
}
